import React from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import CalendlyPopup from '../CalendlyPopup';
import { useCalendlyAndVideo } from '../../hooks/useCalendlyAndVideo';

const OnboardingHero = () => {
  const { isCalendlyOpen, openCalendly, closeCalendly } = useCalendlyAndVideo();
  const journeySteps = ['Discover', 'Design', 'Deploy', 'Elevate'];

  return (
    <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-gray-900 via-gray-950 to-gray-950 overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-900/30 border border-blue-700 rounded-full mb-4 sm:mb-6">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
            <span className="text-xs sm:text-sm text-blue-300 font-medium">White-Glove Service</span>
          </div>
          
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6">
            We don&apos;t just set you up.
            <br />
            <span className="text-blue-400">We build your success system.</span>
          </h1>
          
          <p className="text-base sm:text-xl text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-10">
            We take the time to understand your goals, your workflows, and your team dynamics. Then we create a tailored plan that applies Traain automation and intelligence to elevate performance fast.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-10 sm:mb-16">
            <Button
              size="lg"
              onClick={openCalendly}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6"
            >
              Book My Onboarding Call
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('whats-included')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto border-2 border-gray-700 text-gray-300 hover:bg-gray-800 text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6"
            >
              See What&apos;s Included
            </Button>
          </div>
        </div>

        {/* Journey Flow Visualization */}
        <div className="relative">
          <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
            {journeySteps.map((step, index) => (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-900/50 to-indigo-900/50 border-2 border-blue-700 rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 sm:mb-3 shadow-[0_0_20px_rgba(59,130,246,0.3)]">
                    <span className="text-xl sm:text-2xl font-bold text-blue-400">{index + 1}</span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-white">{step}</span>
                </div>
                {index < journeySteps.length - 1 && (
                  <div className="hidden sm:block w-8 sm:w-16 h-0.5 bg-gradient-to-r from-blue-700 to-indigo-700 mb-6 sm:mb-8" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      
      <CalendlyPopup isOpen={isCalendlyOpen} onClose={closeCalendly} />
    </section>
  );
};

export default OnboardingHero;
